import ChevronDownThinIcon from '@assets/icons/chevron-down-v2.svg';
import ChevronRightThinIcon from '@assets/icons/chevron-right-thin.svg';
import CircleWarningIcon from '@assets/icons/circle-warning.svg';
import i18n from '@/utils/i18n';
import MenuHorizontalIcon from '@assets/icons/menu-horizontal.svg';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import RevertIcon from '@assets/icons/revert.svg';
import {
  applyEditToRow,
  CostChangeValue,
  hasEditablePricing,
  PricingAccountRow,
  PricingGroupRow,
  usePricingReviewRowsTransformer,
} from '@sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import {
  bookmarkKey,
  usePricingBookmarksStore,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-bookmarks-store';
import { buildAccountKey } from '@sales-portal/domain/pricing/blocs/utils/account-key';
import { buildPricingFilterPredicate } from '@sales-portal/domain/pricing/blocs/utils/pricing-filters';
import { buildPricingFilterSections } from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-sections';
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from '@sales-portal/components/button';
import { CollapsibleStickyHeaderRow } from '@/experiences/sales-portal/domain/pricing/components/collapsible-sticky-header-row';
import {
  COLUMN_WIDTH,
  PRICING_CONTENT_MIN_WIDTH,
  PRICING_FILTER_PANEL_WIDTH,
  PRICING_TABLE_LAYOUT,
} from '@sales-portal/domain/pricing/constants';
import {
  ColumnDef,
  createColumnHelper,
  ExpandedState,
  OnChangeFn,
  Row,
  SortingState,
} from '@tanstack/react-table';
import {
  ColumnId,
  PricingGroupedTableRow,
} from '@sales-portal/domain/pricing/screens/management/types';
import {
  ConfigurationDomain,
  TableName,
} from '@/domain/configuration/blocs/stores/column-preferences-keys';
import { ConfigureColumnsButton } from '@experiences/components/table/configure-columns-button';
import { createStyles } from '@/contexts/theme-context';
import { deriveCategoryOptions } from '@sales-portal/domain/pricing/screens/management/blocs/utils/category-filter';
import {
  emptyDelta,
  formatOrEmpty,
  isEmptyDisplayValue,
  valueOrNull,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/missing-value-display';
import { EmptyState } from '@experiences/components/empty-state';
import {
  formatCurrency,
  getCurrencySymbol,
  MICRO_CONVERSION,
  roundMicrosToNearestWholeCent,
} from '@/utils/money';
import { formatPercentage } from '@sales-portal/blocs/utils/money';
import {
  getHeaderLabel,
  PricingTableHeader,
} from '@sales-portal/domain/pricing/screens/management/components/pricing-table-header';
import {
  getLastCostInMicros,
  getLastGrossMarginPercentage,
  getLastPriceInMicros,
  percentOfPrevious,
  roundPercentageToNearestTenth,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/variant-pack-cost-change-pricing';
import { getRenderedRowIndex } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-utils';
import {
  groupedRowKey,
  usePricingSortSnapshot,
} from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-sort-snapshot';
import {
  isDeltaColumn,
  isLeftAlignedColumn,
  PricingReviewFilterValue,
  pricingReviewGlobalFilterFn,
  resolveDelta,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';
import { ItemInputCell } from '@sales-portal/domain/pricing/screens/management/blocs/containers/item-input-cell';
import { ItemUpdatedPricingCellContainer } from '@sales-portal/domain/pricing/screens/management/blocs/containers/item-updated-pricing-cell-container';
import { LayoutChangeEvent, View } from 'react-native';
import {
  leafDimensionOf,
  leafLabelOf,
  PricingGroupBy,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-grouping';
import {
  PricingAnalyticEvent,
  pricingManagementAnalytics,
  PricingPageSource,
} from '@/experiences/sales-portal/domain/pricing/analytics/pricing-management-analytics';
import { PricingBreadcrumbs } from '@sales-portal/domain/pricing/components/pricing-breadcrumbs';
import { PricingEditField } from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import { PricingEditsBadge } from '@sales-portal/domain/pricing/components/pricing-edits-badge';
import { PricingFilterButton } from '@sales-portal/domain/pricing/components/pricing-filter-button';
import { PricingFilterEmptyState } from '@sales-portal/domain/pricing/components/pricing-filter-empty-state';
import { PricingFilterPanel } from '@sales-portal/domain/pricing/components/pricing-filter-panel';
import {
  PricingFilterScope,
  PricingFilterUnit,
  PricingRangeColumnConfig,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { PricingGroupByToggleButton } from '@sales-portal/domain/pricing/components/pricing-group-by-toggle-button';
import { PricingItemDetailsTableRow } from '@sales-portal/domain/pricing/screens/management/components/pricing-item-details-table-row';
import { PricingPeerRangeCell } from '@sales-portal/domain/pricing/screens/management/blocs/containers/pricing-peer-range-cell';
import { PricingPublishButton } from '@sales-portal/domain/pricing/screens/management/components/pricing-publish-button';
import { PricingReviewActionsPopover } from '@sales-portal/domain/pricing/screens/management/components/pricing-review-actions-popover';
import { PricingSearchBar } from '@sales-portal/domain/pricing/components/pricing-search-bar';
import { PricingSummaryHeader } from '@sales-portal/domain/pricing/screens/management/components/pricing-summary-header';
import { PricingTableDeltaCell } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-delta-cell';
import { PricingTableHeaderCells } from '@sales-portal/domain/pricing/components/pricing-table-header-cells';
import { PricingTableInputPlaceholder } from '@sales-portal/domain/pricing/screens/customers/components/pricing-table-input-placeholder';
import { PricingTableNameLink } from '@sales-portal/domain/pricing/components/pricing-table-name-link';
import { PricingTableSkeleton } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-skeleton';
import { PricingUndoButton } from '@sales-portal/domain/pricing/screens/management/components/pricing-undo-button';
import {
  RenderHeaderRowCallback,
  RenderRowCallback,
} from '@experiences/components/table/types';
import {
  REVIEW_SCOPE,
  scopeStorageKey,
  usePricingEditsStore,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import {
  SalesPortalKeyboardScope,
  SalesPortalShortcuts,
} from '@sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { SalesPortalStackParams } from '@/navigators/types';
import {
  SearchInput,
  SearchInputRef,
} from '@sales-portal/components/search-input';
import { SearchSkeleton } from '@sales-portal/components/search-skeleton';
import { StackScreenProps } from '@react-navigation/stack';
import { Table } from '@experiences/components/table/table';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAlertContext } from '@usepepper/sprout-kit-ui';
import { useBusinessOrganizationPricingConfig } from '@sales-portal/blocs/hooks/use-business-organization-pricing-config';
import { useConfigureColumns } from '@experiences/components/table/blocs/hooks/use-configure-columns';
import { useFocusedPricingEditsStore } from '@sales-portal/domain/pricing/blocs/hooks/use-focused-pricing-edits-store';
import { useFocusEffect } from '@react-navigation/native';
import { usePricingEditsTimezoneSync } from '@sales-portal/domain/pricing/screens/management/blocs/hooks/use-pricing-edits-timezone-sync';
import { usePricingFilterPanel } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filter-panel';
import { usePricingFilters } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters';
import { usePricingFiltersUrlSync } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters-url-sync';
import { usePricingGroupBy } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-group-by';
import { usePricingSearch } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-search';
import { useScreenScopedKeyboardShortcuts } from '@experiences/platform/shortcuts/use-screen-scoped-keyboard-shortcuts';
import { useSubmitPriceDraftMutator } from '@sales-portal/domain/pricing/screens/management/blocs/mutators/use-submit-price-draft-mutator';
import { useSyncAndVerifyPriceDraftEdits } from '@sales-portal/domain/pricing/blocs/mutators/use-update-price-draft-mutator';

const columnHelper = createColumnHelper<PricingGroupedTableRow>();

type Props = StackScreenProps<
  SalesPortalStackParams,
  typeof SALES_PORTAL_ROUTES.PricingManagementReview
>;

export const PricingManagementReviewScreen: React.FC<Props> = (props) => {
  const { navigation, route } = props;
  const {
    hasBookmarkingAccess,
    isHistoricalPricingContextEnabled,
    isPeerRangeEnabled,
    costChangeLookbackMode,
    costChangeWeekAnchorDay,
  } = useBusinessOrganizationPricingConfig();
  const [styles, theme] = useStyles();
  const [tableLayoutWidth, setTableLayoutWidth] = useState<number | null>(null);
  const handleTableLayout = useCallback(
    (event: LayoutChangeEvent) =>
      setTableLayoutWidth(event.nativeEvent.layout.width),
    [],
  );
  const [isReviewActionsOpen, setIsReviewActionsOpen] = useState(false);
  const { clearSearch, debouncedSearchText, searchText, setSearchText } =
    usePricingSearch();
  const [sorting, setSorting] = useState<SortingState>([
    { desc: false, id: ColumnId.WeeklyEditsGrossProfitImpact },
  ]);
  const [expanded, setExpanded] = useState<ExpandedState>(true);

  const { alert } = useAlertContext('revert_all_edits');

  const handleSearchChange = (text: string) => {
    setSearchText(text);
  };

  const hasUndoHistory = useFocusedPricingEditsStore(
    (state) =>
      (state.editSnapshotStack[scopeStorageKey(REVIEW_SCOPE)]?.length ?? 0) > 0,
  );
  const undoLastEdit = usePricingEditsStore((state) => state.undo);
  const resetUndoHistory = usePricingEditsStore(
    (state) => state.resetUndoHistory,
  );
  const handleUndo = useCallback(() => {
    undoLastEdit(REVIEW_SCOPE);
    pricingManagementAnalytics.track(PricingAnalyticEvent.UNDO, {
      pageSource: PricingPageSource.REVIEW,
    });
  }, [undoLastEdit]);

  useFocusEffect(
    useCallback(() => {
      resetUndoHistory(REVIEW_SCOPE);
    }, [resetUndoHistory]),
  );
  const revertAllEdits = usePricingEditsStore((state) => state.revertAllEdits);
  const edits = useFocusedPricingEditsStore((state) => state.edits);
  const { groupBy, setGroupBy } = usePricingGroupBy(TableName.PricingReview);

  const toggleGroupBy = useCallback(() => {
    const next = leafDimensionOf(groupBy);
    setGroupBy(next);
    // The parents are different rows now, so start the new view expanded.
    setExpanded(true);
    pricingManagementAnalytics.track(PricingAnalyticEvent.GROUP_BY_TOGGLE, {
      groupBy: next,
      pageSource: PricingPageSource.REVIEW,
    });
  }, [groupBy, setGroupBy]);

  const navigateToEntity = useCallback(
    (dimension: PricingGroupBy, uuid: string) => {
      if (dimension === PricingGroupBy.VariantPack) {
        navigation.navigate(SALES_PORTAL_ROUTES.PricingItemManagement, {
          variantPackUUID: uuid,
        });
      } else {
        navigation.navigate(SALES_PORTAL_ROUTES.PricingCustomerDetail, {
          restaurantUUID: uuid,
        });
      }
    },
    [navigation],
  );
  const validUntil = useFocusedPricingEditsStore((state) => state.validUntil);

  const [editsHydrated, setEditsHydrated] = useState(() =>
    usePricingEditsStore.persist.hasHydrated(),
  );
  useEffect(
    () =>
      usePricingEditsStore.persist.onFinishHydration(() =>
        setEditsHydrated(true),
      ),
    [],
  );

  useEffect(() => {
    if (editsHydrated && edits.length === 0) {
      navigation.navigate(SALES_PORTAL_ROUTES.PricingManagement);
    }
  }, [editsHydrated, edits, navigation]);

  const {
    variantPackCount,
    restaurantCount,
    totalWeeklyVolume,
    totalWeeklyImpact,
  } = useMemo(() => {
    const { variantPacks, restaurants, weeklyVolume, weeklyImpactInMicros } =
      edits.reduce(
        (acc, edit) => {
          acc.variantPacks.add(edit.variantPackUUID);
          acc.restaurants.add(edit.restaurantUUID);
          acc.weeklyVolume += edit.weeklyVolume || 0;
          acc.weeklyImpactInMicros += edit.grossProfitImpactInMicros || 0;
          return acc;
        },
        {
          restaurants: new Set<string>(),
          variantPacks: new Set<string>(),
          weeklyImpactInMicros: 0,
          weeklyVolume: 0,
        },
      );

    return {
      restaurantCount: restaurants.size,
      totalWeeklyImpact: weeklyImpactInMicros,
      totalWeeklyVolume: weeklyVolume,
      variantPackCount: variantPacks.size,
    };
  }, [edits]);

  const { currencyCode, timezone, timezoneLoading } =
    usePricingEditsTimezoneSync();

  const { data, loading, error, syncAndVerify } =
    useSyncAndVerifyPriceDraftEdits();

  useEffect(() => {
    syncAndVerify();
    // we sync the local pricing updates only once when the screen is mounted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasTrackedLoad = useRef(false);
  useEffect(() => {
    if (!loading && !error && data && !hasTrackedLoad.current) {
      hasTrackedLoad.current = true;
      pricingManagementAnalytics.track(
        PricingAnalyticEvent.LOAD_REVIEW_PAGE,
        {},
      );
    }
  }, [loading, error, data]);

  const { loading: submitPriceDraftLoading, submitPriceDrafts } =
    useSubmitPriceDraftMutator();

  const searchInputRef = useRef<SearchInputRef>(null);

  // Keyboard shortcut: / to focus search input
  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.Search,
    () => searchInputRef.current?.focus(),
    [],
    {},
    true,
    SalesPortalKeyboardScope.PRICING_MANAGEMENT,
  );

  const dataToDisplay = usePricingReviewRowsTransformer(
    data?.upsertPricingPriceDraftUpdatesForBusinessOrganization.cost_changes ??
      [],
    groupBy,
  );

  const publishEnabled =
    data?.upsertPricingPriceDraftUpdatesForBusinessOrganization
      .publish_enabled ?? false;

  // The transformer hands back original (unedited) leaf rows, so re-apply the
  // pending edits for the column sort comparators.
  const mergedAccountRows = useMemo(() => {
    const editsByAccount = new Map(
      edits.map((edit) => [buildAccountKey(edit), edit]),
    );
    return dataToDisplay.flatMap((group) =>
      group.subRows.map((row) => {
        const edit = editsByAccount.get(buildAccountKey(row));
        return edit ? applyEditToRow(row, edit) : row;
      }),
    );
  }, [dataToDisplay, edits]);

  // Regrouping replaces the parent rows, so it re-takes the snapshot too.
  const sortSnapshotSource = useMemo(
    () => ({ data, groupBy }),
    [data, groupBy],
  );
  const sortSnapshotRows = useMemo<PricingGroupedTableRow[]>(
    () => [...dataToDisplay, ...mergedAccountRows],
    [dataToDisplay, mergedAccountRows],
  );
  const { onSortingChange, resolveEditedRow } = usePricingSortSnapshot({
    keyOf: groupedRowKey,
    rows: sortSnapshotRows,
    setSorting,
    source: sortSnapshotSource,
  });

  const regularColumnCount =
    PRICING_TABLE_LAYOUT.REGULAR_COLUMN_COUNT + (isPeerRangeEnabled ? 1 : 0);
  const remainingPercentage = 100 - PRICING_TABLE_LAYOUT.NAME_COLUMN_PERCENTAGE;
  const columnWidthPercentage = remainingPercentage / regularColumnCount;

  const onExpandedChange = useCallback<OnChangeFn<ExpandedState>>(
    (expanded) => setExpanded(expanded),
    [setExpanded],
  );

  const onInitiatePublishEdits = useCallback(() => {
    alert({
      buttons: [
        {
          action: () => {},
          style: 'cancel',
          title: i18n.t('cancel'),
        },
        {
          action: async () => {
            await submitPriceDrafts(revertAllEdits);
          },
          style: 'default',
          title: i18n.t('pricing.publish.modal.cta'),
        },
      ],
      message: i18n.t('pricing.publish.modal.description'),
      title: i18n.t('pricing.publish.modal.title'),
    });
  }, [alert, revertAllEdits, submitPriceDrafts]);

  const pricingReviewActions = useMemo(
    () => [
      {
        icon: (
          <RevertIcon
            height={14}
            stroke={theme.sk.color.icon.tertiary}
            width={14}
          />
        ),
        id: 'revert_all_edits',
        label: i18n.t('pricing.review.revert_all_edits'),
        onSelect: () =>
          alert({
            buttons: [
              {
                action: () => {},
                style: 'cancel',
                title: i18n.t('cancel'),
              },
              {
                action: revertAllEdits,
                style: 'destructive',
                title: i18n.t('pricing.review.revert_all_edits.modal.cta'),
              },
            ],
            message: i18n.t(
              'pricing.review.revert_all_edits.modal.description',
            ),
            title: i18n.t('pricing.review.revert_all_edits.modal.title'),
          }),
      },
    ],
    [alert, revertAllEdits, theme.sk.color.icon.tertiary],
  );

  const columns = useMemo<ColumnDef<PricingGroupedTableRow, string>[]>(() => {
    return [
      // Name
      columnHelper.accessor(
        (row) =>
          'subRows' in row ? row.groupName : leafLabelOf(row, groupBy).name,
        {
          cell: ({ row, getValue }) => {
            if (row.depth === 0) {
              const group = row.original as PricingGroupRow;
              // Review only lists edited rows, so every sub-row carries an edit.
              return (
                <View style={styles.parentNameWrapper}>
                  {row.getIsExpanded() ? (
                    <ChevronDownThinIcon size={16} stroke="black" />
                  ) : (
                    <ChevronRightThinIcon size={16} stroke="black" />
                  )}
                  <View style={styles.nameWithBadgeWrapper}>
                    <PricingTableNameLink
                      name={getValue()}
                      onPress={() =>
                        navigateToEntity(group.groupBy, group.groupKey)
                      }
                    />
                    <PricingEditsBadge editsCount={group.subRows.length} />
                  </View>
                </View>
              );
            }

            const originalRow = row.original as PricingAccountRow;
            const leaf = leafLabelOf(originalRow, groupBy);

            return (
              <View style={styles.nestedNameWrapper}>
                <PricingTableNameLink
                  name={leaf.name}
                  onPress={() =>
                    navigateToEntity(leafDimensionOf(groupBy), leaf.key)
                  }
                />
              </View>
            );
          },
          header: () => (
            <View style={styles.nestedNameWrapper}>
              <PricingTableHeader columnId={ColumnId.Name} />
            </View>
          ),
          id: ColumnId.Name,
          meta: {
            alwaysVisible: true,
            initialPercentWidth: PRICING_TABLE_LAYOUT.NAME_COLUMN_PERCENTAGE,
            label: getHeaderLabel(ColumnId.Name),
          },
          minSize: PRICING_TABLE_LAYOUT.NAME_COLUMN_MIN_WIDTH,
          sortingFn: 'alphanumeric',
        },
      ),
      // ID
      columnHelper.accessor(
        (row) =>
          'subRows' in row ? row.groupId : leafLabelOf(row, groupBy).id,
        {
          cell: ({ getValue }) => (
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-md"
            >
              {getValue()}
            </Typography>
          ),
          header: () => <PricingTableHeader columnId={ColumnId.Id} />,
          id: ColumnId.Id,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.Id),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: 'alphanumeric',
        },
      ),
      // UOM
      columnHelper.accessor((row) => row.variantPackUOM, {
        cell: (info) => (
          <Typography
            color={theme.sk.color.text.primary}
            textTransform="capitalize"
            variant="paragraph-md"
          >
            {info.getValue()}
          </Typography>
        ),
        header: () => <PricingTableHeader columnId={ColumnId.UOM} />,
        id: ColumnId.UOM,
        meta: {
          initialPercentWidth: columnWidthPercentage,
          label: getHeaderLabel(ColumnId.UOM),
        },
        ...COLUMN_WIDTH.M,
        sortingFn: 'alphanumeric',
      }),
      // Peer Range
      ...(isPeerRangeEnabled
        ? [
            columnHelper.display({
              cell: (info) => {
                if (info.row.depth === 0) {
                  return null;
                }
                const originalRow = info.row.original as PricingAccountRow;
                return (
                  <PricingPeerRangeCell
                    grossMarginPercentage={originalRow.grossMarginPercentage}
                    peerRange={originalRow.peerRange}
                    restaurantUUID={originalRow.restaurantUUID}
                    variantPackUUID={originalRow.variantPackUUID}
                  />
                );
              },
              enableSorting: false,
              header: () => (
                <PricingTableHeader columnId={ColumnId.PeerRange} />
              ),
              id: ColumnId.PeerRange,
              meta: {
                initialPercentWidth: columnWidthPercentage,
                label: getHeaderLabel(ColumnId.PeerRange),
              },
              ...COLUMN_WIDTH.M,
            }),
          ]
        : []),
      // Last GM%
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(
            row,
            [CostChangeValue.GrossMargin, CostChangeValue.GrossMarginDelta],
            () => formatPercentage(getLastGrossMarginPercentage(row), 1, 1),
          ),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => <PricingTableHeader columnId={ColumnId.LastMargin} />,
          id: ColumnId.LastMargin,
          meta: {
            defaultHidden: true,
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.LastMargin),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) =>
            getLastGrossMarginPercentage(rowA.original) -
            getLastGrossMarginPercentage(rowB.original),
        },
      ),
      // Gross Margin %
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.GrossMargin], () =>
            formatPercentage(row.grossMarginPercentage, 1, 1),
          ),
        {
          cell: ({ row, getValue, ...context }) => {
            const value = getValue();
            if (row.depth === 0) {
              return <Typography variant="paragraph-md">{value}</Typography>;
            }

            const originalRow = row.original as PricingAccountRow;
            if (!hasEditablePricing(originalRow)) {
              return <PricingTableInputPlaceholder value={value} />;
            }
            return (
              <ItemInputCell
                canBookmarkItem={hasBookmarkingAccess}
                columnId={context.column.id}
                currencyCode={currencyCode}
                disabled={originalRow.isLocked || submitPriceDraftLoading}
                inputType={PricingEditField.GrossMarginPercentage}
                isHistoricalPricingContextEnabled={
                  isHistoricalPricingContextEnabled
                }
                originalFormattedValue={formatPercentage(
                  row.original.grossMarginPercentage,
                  1,
                )}
                originalRow={originalRow}
                pageSource={PricingPageSource.REVIEW}
                priceFloorEnabled={originalRow.priceFloorEnabled}
                priceFloorMode={originalRow.priceFloorMode}
                restaurantUUID={originalRow.restaurantUUID}
                rowIndex={row.id}
                scope={REVIEW_SCOPE}
                tableRowIndex={getRenderedRowIndex(context, row.index)}
                value={String(
                  parseFloat(row.original.grossMarginPercentage.toFixed(1)),
                )}
                variantPackUUID={row.original.variantPackUUID}
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.CurrentGrossMargin}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.CurrentGrossMargin,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.CurrentGrossMargin),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original);
            const b = resolveEditedRow(rowB.original);
            return a.grossMarginPercentage - b.grossMarginPercentage;
          },
        },
      ),
      // Gross Margin % delta
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.GrossMarginDelta], () =>
            formatPercentage(
              roundPercentageToNearestTenth(row.grossMarginPercentageDelta),
              1,
              1,
            ),
          ),
        {
          cell: ({ row, getValue }) => {
            const { direction, formattedValue } = isEmptyDisplayValue(
              getValue(),
            )
              ? emptyDelta
              : resolveDelta(
                  roundPercentageToNearestTenth(
                    row.original.grossMarginPercentageDelta,
                  ),
                  (v) => formatPercentage(v, 1, 1),
                );
            if (row.depth === 0) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={direction}
                  value={formattedValue}
                />
              );
            }

            const originalRow = row.original as PricingAccountRow;
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="grossMarginPercentageDelta"
                formatValue={(value) =>
                  formatPercentage(roundPercentageToNearestTenth(value), 1, 1)
                }
                originalArrowDirection={direction}
                originalFormattedValue={formattedValue}
                originalRow={originalRow}
                originalValue={formattedValue}
                testID="original-gross-margin-percentage-delta"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.GrossMarginPercentageDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.GrossMarginPercentageDelta,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.GrossMarginPercentageDelta),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original);
            const b = resolveEditedRow(rowB.original);
            return a.grossMarginPercentageDelta - b.grossMarginPercentageDelta;
          },
        },
      ),
      // Last Price
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(
            row,
            [CostChangeValue.Price, CostChangeValue.PriceDelta],
            () => formatCurrency(getLastPriceInMicros(row), currencyCode),
          ),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => <PricingTableHeader columnId={ColumnId.LastPrice} />,
          id: ColumnId.LastPrice,
          meta: {
            defaultHidden: true,
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.LastPrice),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) =>
            getLastPriceInMicros(rowA.original) -
            getLastPriceInMicros(rowB.original),
        },
      ),
      // Current Price
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.Price], () =>
            formatCurrency(row.priceInMicros, currencyCode),
          ),
        {
          cell: ({ row, getValue, ...context }) => {
            const value = getValue();
            if (row.depth === 0) {
              return (
                <Typography
                  color={
                    row.index === 0 ? theme.sk.color.text.primary : undefined
                  }
                  testID={`price-group-${(row.original as PricingGroupRow).groupKey}`}
                  variant="paragraph-md"
                >
                  {value}
                </Typography>
              );
            }
            const priceInMicros = row.original.priceInMicros;
            const originalRow = row.original as PricingAccountRow;
            if (!hasEditablePricing(originalRow)) {
              return (
                <PricingTableInputPlaceholder
                  color={theme.sk.color.text.primary}
                  value={value}
                />
              );
            }
            return (
              <ItemInputCell
                canBookmarkItem={hasBookmarkingAccess}
                columnId={context.column.id}
                currencyCode={currencyCode}
                disabled={originalRow.isLocked || submitPriceDraftLoading}
                inputType={PricingEditField.Price}
                isHistoricalPricingContextEnabled={
                  isHistoricalPricingContextEnabled
                }
                originalFormattedValue={formatCurrency(
                  priceInMicros,
                  currencyCode,
                )}
                originalRow={originalRow}
                pageSource={PricingPageSource.REVIEW}
                priceFloorEnabled={originalRow.priceFloorEnabled}
                priceFloorMode={originalRow.priceFloorMode}
                restaurantUUID={originalRow.restaurantUUID}
                rowIndex={row.id}
                scope={REVIEW_SCOPE}
                tableRowIndex={getRenderedRowIndex(context, row.index)}
                value={value}
                variantPackUUID={originalRow.variantPackUUID}
              />
            );
          },
          header: () => <PricingTableHeader columnId={ColumnId.CurrentPrice} />,
          id: ColumnId.CurrentPrice,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.CurrentPrice),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original);
            const b = resolveEditedRow(rowB.original);
            return a.priceInMicros - b.priceInMicros;
          },
        },
      ),
      // Price delta
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.PriceDelta], () =>
            formatCurrency(row.priceDeltaInMicros, currencyCode),
          ),
        {
          cell: ({ row, getValue }) => {
            const { direction, formattedValue } = isEmptyDisplayValue(
              getValue(),
            )
              ? emptyDelta
              : resolveDelta(row.original.priceDeltaInMicros, (v) =>
                  formatCurrency(v, currencyCode),
                );
            if (row.depth === 0) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={direction}
                  value={formattedValue}
                />
              );
            }

            const originalRow = row.original as PricingAccountRow;
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="priceDeltaInMicros"
                formatValue={(value) => formatCurrency(value, currencyCode)}
                originalArrowDirection={direction}
                originalFormattedValue={formattedValue}
                originalRow={originalRow}
                originalValue={formattedValue}
                testID="original-price-delta"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.PriceDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.PriceDelta,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.PriceDelta),
          },
          ...COLUMN_WIDTH.S,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original);
            const b = resolveEditedRow(rowB.original);
            return a.priceDeltaInMicros - b.priceDeltaInMicros;
          },
        },
      ),
      // Last Cost
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(
            row,
            [CostChangeValue.Cost, CostChangeValue.CostDelta],
            () => formatCurrency(getLastCostInMicros(row), currencyCode),
          ),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => <PricingTableHeader columnId={ColumnId.LastCost} />,
          id: ColumnId.LastCost,
          meta: {
            defaultHidden: true,
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.LastCost),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) =>
            getLastCostInMicros(rowA.original) -
            getLastCostInMicros(rowB.original),
        },
      ),
      // Current Cost
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.Cost], () =>
            formatCurrency(row.costInMicros, currencyCode),
          ),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => <PricingTableHeader columnId={ColumnId.CurrentCost} />,
          id: ColumnId.CurrentCost,
          meta: {
            defaultHidden: true,
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.CurrentCost),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) =>
            rowA.original.costInMicros - rowB.original.costInMicros,
        },
      ),
      // Cost delta
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.CostDelta], () =>
            formatCurrency(row.costDeltaInMicros, currencyCode),
          ),
        {
          cell: (info) => {
            const { direction, formattedValue } = isEmptyDisplayValue(
              info.getValue(),
            )
              ? emptyDelta
              : resolveDelta(info.row.original.costDeltaInMicros, (v) =>
                  formatCurrency(v, currencyCode),
                );

            return (
              <PricingTableDeltaCell
                arrowDirection={direction}
                hasInvertedColor
                value={formattedValue}
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.CostDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.CostDelta,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.CostDelta),
          },
          ...COLUMN_WIDTH.S,
          sortingFn: (rowA, rowB) =>
            rowA.original.costDeltaInMicros - rowB.original.costDeltaInMicros,
        },
      ),
      // Gross Profit
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.GrossProfit], () =>
            formatCurrency(row.grossProfitInMicros, currencyCode),
          ),
        {
          cell: ({ row, getValue }) => {
            const value = getValue();
            if (row.depth === 0) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={undefined}
                  hideDeltaAttributes
                  value={value}
                />
              );
            }

            const originalRow = row.original as PricingAccountRow;
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="grossProfitInMicros"
                formatValue={(value) => formatCurrency(value, currencyCode)}
                hideDeltaAttributes
                originalFormattedValue={value}
                originalRow={originalRow}
                originalValue={value}
                testID="original-gross-profit"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.GrossProfit}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.GrossProfit,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.GrossProfit),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original);
            const b = resolveEditedRow(rowB.original);
            return a.grossProfitInMicros - b.grossProfitInMicros;
          },
        },
      ),
      // Weekly volume
      columnHelper.accessor((row) => row.quantity.toFixed(1), {
        cell: (info) => (
          <Typography variant="paragraph-md">{info.getValue()}</Typography>
        ),
        header: () => (
          <PricingTableHeader
            columnId={ColumnId.WeeklyVolume}
            costChangeLookbackMode={costChangeLookbackMode}
            costChangeWeekAnchorDay={costChangeWeekAnchorDay}
            withTooltip
          />
        ),
        id: ColumnId.WeeklyVolume,
        meta: {
          initialPercentWidth: columnWidthPercentage,
          label: getHeaderLabel(ColumnId.WeeklyVolume),
        },
        ...COLUMN_WIDTH.XS,
        sortingFn: (rowA, rowB) =>
          rowA.original.quantity - rowB.original.quantity,
      }),
      // Edits GP Impact
      columnHelper.accessor(
        (row) =>
          formatCurrency(
            roundMicrosToNearestWholeCent(row.editsGrossProfitImpactInMicros),
            currencyCode,
          ),
        {
          cell: ({ row }) => {
            const { direction, formattedValue } = resolveDelta(
              roundMicrosToNearestWholeCent(
                row.original.editsGrossProfitImpactInMicros,
              ),
              (v) => formatCurrency(v, currencyCode),
            );
            if (row.depth === 0) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={direction}
                  value={formattedValue}
                />
              );
            }

            const originalRow = row.original as PricingAccountRow;
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="editsGrossProfitImpactInMicros"
                formatValue={(value) =>
                  formatCurrency(
                    roundMicrosToNearestWholeCent(value),
                    currencyCode,
                  )
                }
                originalArrowDirection={direction}
                originalFormattedValue={formattedValue}
                originalRow={originalRow}
                originalValue={formattedValue}
                testID="original-edits-gross-profit-impact"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.WeeklyEditsGrossProfitImpact}
              withTooltip
            />
          ),
          id: ColumnId.WeeklyEditsGrossProfitImpact,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.WeeklyEditsGrossProfitImpact),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original);
            const b = resolveEditedRow(rowB.original);
            return (
              a.editsGrossProfitImpactInMicros -
              b.editsGrossProfitImpactInMicros
            );
          },
        },
      ),
    ];
  }, [
    columnWidthPercentage,
    costChangeLookbackMode,
    costChangeWeekAnchorDay,
    currencyCode,
    groupBy,
    hasBookmarkingAccess,
    isHistoricalPricingContextEnabled,
    isPeerRangeEnabled,
    navigateToEntity,
    resolveEditedRow,
    styles,
    submitPriceDraftLoading,
    theme.sk.color.text.primary,
  ]);

  const { columnOrder, columnVisibility } = useConfigureColumns(
    ConfigurationDomain.Pricing,
    TableName.PricingReview,
    columns,
  );

  const categoryOptions = useMemo(
    () => deriveCategoryOptions(mergedAccountRows),
    [mergedAccountRows],
  );

  const bookmarks = usePricingBookmarksStore((state) => state.bookmarks);
  // Keyed by the account pair: one item can be bookmarked for one restaurant
  // and not another, and every row here is a single restaurant's edit.
  const bookmarkedByAccount = useMemo(() => {
    if (!hasBookmarkingAccess) {
      return undefined;
    }
    return mergedAccountRows.reduce<Record<string, boolean>>((acc, row) => {
      acc[buildAccountKey(row)] =
        bookmarkKey(row.variantPackUUID, row.restaurantUUID) in bookmarks;
      return acc;
    }, {});
  }, [bookmarks, hasBookmarkingAccess, mergedAccountRows]);

  const rangeColumnConfigs = useMemo<
    Record<string, PricingRangeColumnConfig<PricingAccountRow>>
  >(() => {
    const currencyAffix = getCurrencySymbol(currencyCode);

    return {
      [ColumnId.LastMargin]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.GrossMargin, CostChangeValue.GrossMarginDelta],
            () => getLastGrossMarginPercentage(row),
          ),
        affix: '%',
        title: getHeaderLabel(ColumnId.LastMargin),
        unit: PricingFilterUnit.Percent,
      },
      [ColumnId.CurrentGrossMargin]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.GrossMargin],
            () => row.grossMarginPercentage,
          ),
        affix: '%',
        title: getHeaderLabel(ColumnId.CurrentGrossMargin),
        unit: PricingFilterUnit.Percent,
      },
      [ColumnId.GrossMarginPercentageDelta]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.GrossMarginDelta],
            () => row.grossMarginPercentageDelta,
          ),
        affix: '%',
        title: getHeaderLabel(ColumnId.GrossMarginPercentageDelta),
        unit: PricingFilterUnit.Percent,
      },
      [ColumnId.LastPrice]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.Price, CostChangeValue.PriceDelta],
            () => getLastPriceInMicros(row) / MICRO_CONVERSION,
          ),
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.LastPrice),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.CurrentPrice]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.Price],
            () => row.priceInMicros / MICRO_CONVERSION,
          ),
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.CurrentPrice),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.PriceDelta]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.PriceDelta],
            () => row.priceDeltaInMicros / MICRO_CONVERSION,
          ),
        affix: currencyAffix,
        filterPillMetric: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.Price, CostChangeValue.PriceDelta],
            () =>
              percentOfPrevious(
                row.priceDeltaInMicros,
                getLastPriceInMicros(row),
              ),
          ),
        title: getHeaderLabel(ColumnId.PriceDelta),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.LastCost]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.Cost, CostChangeValue.CostDelta],
            () => getLastCostInMicros(row) / MICRO_CONVERSION,
          ),
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.LastCost),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.CurrentCost]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.Cost],
            () => row.costInMicros / MICRO_CONVERSION,
          ),
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.CurrentCost),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.CostDelta]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.CostDelta],
            () => row.costDeltaInMicros / MICRO_CONVERSION,
          ),
        affix: currencyAffix,
        filterPillMetric: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.Cost, CostChangeValue.CostDelta],
            () =>
              percentOfPrevious(
                row.costDeltaInMicros,
                getLastCostInMicros(row),
              ),
          ),
        title: getHeaderLabel(ColumnId.CostDelta),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.GrossProfit]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.GrossProfit],
            () => row.grossProfitInMicros / MICRO_CONVERSION,
          ),
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.GrossProfit),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.WeeklyVolume]: {
        accessor: (row) => row.quantity,
        affix: 'u',
        title: getHeaderLabel(ColumnId.WeeklyVolume),
        unit: PricingFilterUnit.Unit,
      },
      [ColumnId.WeeklyEditsGrossProfitImpact]: {
        accessor: (row) =>
          row.editsGrossProfitImpactInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.WeeklyEditsGrossProfitImpact),
        unit: PricingFilterUnit.Currency,
      },
    };
  }, [currencyCode]);

  // Declared order, never `columnOrder` — rearranging the table must not move
  // the filters.
  const filterColumnIds = useMemo(
    () =>
      columns
        .map((column) => column.id)
        .filter((id): id is string => Boolean(id)),
    [columns],
  );

  // No Edits pill: every row on this screen is already in the price draft, so
  // it wouldn't discriminate anything.
  const sections = useMemo(
    () =>
      buildPricingFilterSections({
        bookmarked: bookmarkedByAccount
          ? {
              bookmarkedByRow: bookmarkedByAccount,
              getRowId: (row) => buildAccountKey(row),
            }
          : undefined,
        categoryOptions,
        columnIds: filterColumnIds,
        peerAverage: isPeerRangeEnabled
          ? {
              getCurrentGrossMargin: (row) => row.grossMarginPercentage,
              getPeerRange: (row) => row.peerRange,
            }
          : undefined,
        rangeColumnConfigs,
        rows: mergedAccountRows,
        timezone,
      }),
    [
      bookmarkedByAccount,
      categoryOptions,
      filterColumnIds,
      isPeerRangeEnabled,
      mergedAccountRows,
      rangeColumnConfigs,
      timezone,
    ],
  );

  const filters = usePricingFilters(PricingFilterScope.Review, sections);
  const panel = usePricingFilterPanel();

  const handleFiltersUrlUpdate = useCallback(
    (params: Record<string, string | undefined>) =>
      navigation.setParams(params),
    [navigation],
  );

  usePricingFiltersUrlSync({
    hydrate: filters.hydrate,
    onUrlUpdate: handleFiltersUrlUpdate,
    routeParams: route.params ?? {},
    sections,
    selections: { pills: filters.pills, ranges: filters.ranges },
  });

  // Drop filters whose column is gone, so they can't silently keep shrinking
  // the table. Depends on the store action, not `filters`, which would loop.
  useEffect(() => {
    filters.pruneSections(sections.map((section) => section.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, filters.pruneSections]);

  const { pills: filterPills, ranges: filterRanges } = filters;

  // The table filters and rebuilds the parents itself (`filterFromLeafRows`),
  // so search and filters run in one pass over one row model.
  const globalFilter = useMemo<PricingReviewFilterValue | undefined>(() => {
    if (debouncedSearchText.length === 0 && !filters.hasActiveFilters) {
      return undefined;
    }

    if (!filters.hasActiveFilters) {
      return { text: debouncedSearchText };
    }

    const matchesFilters = buildPricingFilterPredicate(
      { pills: filterPills, ranges: filterRanges },
      sections,
    );

    return {
      matches: (row) =>
        matchesFilters(resolveEditedRow(row.original) as PricingAccountRow),
      text: debouncedSearchText,
    };
  }, [
    debouncedSearchText,
    filterPills,
    filterRanges,
    filters.hasActiveFilters,
    resolveEditedRow,
    sections,
  ]);

  const handleClearEmptyState = useCallback(() => {
    if (debouncedSearchText.length > 0) {
      clearSearch();
    } else {
      filters.clearAll();
    }
  }, [clearSearch, debouncedSearchText, filters]);

  const LoadingSkeleton = () => (
    <View
      style={styles.loadingContent}
      testID="pricing-management-loading-skeleton"
    >
      <SearchSkeleton />
      <PricingTableSkeleton />
    </View>
  );

  const renderHeaderRow = useCallback<
    RenderHeaderRowCallback<PricingGroupedTableRow>
  >(
    ({ headerGroup }) => (
      <PricingTableHeaderCells
        headerCellStyle={styles.headerCell}
        headerGroup={headerGroup}
        isLeftAlignedColumn={isLeftAlignedColumn}
        key={headerGroup.id}
        sorting={sorting}
      />
    ),
    [sorting, styles.headerCell],
  );

  // A fresh array here would churn the table context and every cell in it.
  const cellPadding = useMemo(
    () => Array(columns.length).fill(theme.sk.spacing.spaceSmall),
    [columns.length, theme.sk.spacing.spaceSmall],
  );

  const isStickyRow = useCallback(
    (row: Row<PricingGroupedTableRow>) => row.depth === 0,
    [],
  );

  const renderRow = useCallback<RenderRowCallback<PricingGroupedTableRow>>(
    ({ index, row }) => {
      if (row.depth > 0) {
        return (
          <PricingItemDetailsTableRow
            columnOrder={columnOrder}
            columnVisibility={columnVisibility}
            hasChanges
            index={index}
            isSelected
            key={row.id}
            row={row}
          />
        );
      }
      return (
        <CollapsibleStickyHeaderRow
          index={index}
          isDeltaColumn={isDeltaColumn}
          isLeftAlignedColumn={isLeftAlignedColumn}
          key={row.id}
          onPress={row.getToggleExpandedHandler()}
          row={row}
          testID={`pricing-table-row-${row.index}`}
        />
      );
    },
    [columnOrder, columnVisibility],
  );

  return (
    <SalesHubShellTemplate>
      <AppPageLayout
        actions={
          dataToDisplay.length > 0 ? (
            <View
              style={[
                styles.topBarActions,
                panel.isOpen && styles.actionsWithPanelOpen,
              ]}
            >
              <PricingReviewActionsPopover
                actions={pricingReviewActions}
                isOpen={isReviewActionsOpen}
                onActionSelect={(action) => {
                  action.onSelect();
                  setIsReviewActionsOpen(false);
                }}
                onOpenChange={() => {
                  setIsReviewActionsOpen(false);
                }}
                trigger={
                  <Button
                    accessibilityLabel="Review actions button"
                    onPress={() => setIsReviewActionsOpen(true)}
                    size={ButtonSize.Small}
                    style={styles.iconButton}
                    testID="pricing-review-actions-button"
                    variant={ButtonVariant.Secondary}
                  >
                    <MenuHorizontalIcon
                      stroke={theme.sk.color.icon.secondary}
                    />
                  </Button>
                }
              />
              {hasUndoHistory ? (
                <PricingUndoButton onPress={handleUndo} />
              ) : null}
              <PricingPublishButton
                disabled={!publishEnabled}
                loading={submitPriceDraftLoading}
                onPress={onInitiatePublishEdits}
              />
            </View>
          ) : null
        }
        leadingActions={
          <PricingBreadcrumbs secondaryBreadcrumb={i18n.t('pricing.review')} />
        }
        overlay={
          panel.isOpen ? (
            <PricingFilterPanel
              filters={filters}
              onClose={panel.close}
              sections={sections}
            />
          ) : null
        }
        scroll={false}
      >
        <View
          style={[
            styles.container,
            panel.isOpen && styles.containerWithPanelOpen,
          ]}
        >
          {loading || timezoneLoading ? (
            <LoadingSkeleton />
          ) : error ? (
            <EmptyState
              bottomElement={
                <Button
                  accessibilityLabel={i18n.t('sales_portal.error.retry')}
                  onPress={async () => await syncAndVerify()}
                  variant={ButtonVariant.Secondary}
                  width={120}
                >
                  {i18n.t('sales_portal.error.retry')}
                </Button>
              }
              description={i18n.t('pricing.error.table.description')}
              iconElement={
                <CircleWarningIcon stroke={theme.sk.color.icon.primary} />
              }
              title={i18n.t('pricing.error.table.title')}
            />
          ) : (
            <Suspense fallback={<LoadingSkeleton />}>
              <PricingSearchBar
                trailing={
                  <>
                    <PricingFilterButton
                      activeCount={filters.activeCount}
                      onPress={panel.open}
                    />
                    <ConfigureColumnsButton
                      columns={columns}
                      domain={ConfigurationDomain.Pricing}
                      table={TableName.PricingReview}
                    />
                    <PricingGroupByToggleButton
                      groupBy={groupBy}
                      onPress={toggleGroupBy}
                    />
                  </>
                }
              >
                <SearchInput
                  onSearchChange={handleSearchChange}
                  placeholderText={i18n.t('pricing.search_placeholder')}
                  ref={searchInputRef}
                  searchText={searchText}
                />
              </PricingSearchBar>
              <PricingSummaryHeader
                currencyCode={currencyCode}
                editsImpact={totalWeeklyImpact}
                restaurantCount={restaurantCount}
                title={i18n.t('pricing.summary.impact_of_your_edits')}
                totalWeeklyVolume={totalWeeklyVolume}
                validUntil={validUntil}
                variantPackCount={variantPackCount}
              />
              <View onLayout={handleTableLayout} style={styles.tableWrapper}>
                <Table
                  cellPadding={cellPadding}
                  columns={columns}
                  data={dataToDisplay}
                  emptyComponent={
                    <PricingFilterEmptyState
                      baseEmptyComponent={
                        <Typography
                          color={theme.sk.color.text.primary}
                          textAlign="center"
                          variant="paragraph-base"
                        >
                          {i18n.t('pricing.review.empty.description')}
                        </Typography>
                      }
                      hasActiveFilters={filters.hasActiveFilters}
                      onClear={handleClearEmptyState}
                      searchText={debouncedSearchText}
                    />
                  }
                  estimatedHeaderHeight={PRICING_TABLE_LAYOUT.HEADER_HEIGHT}
                  estimatedRowHeight={PRICING_TABLE_LAYOUT.ESTIMATED_ROW_HEIGHT}
                  fillAvailableWidth
                  filterFromLeafRows
                  getSubRows={(row) =>
                    'subRows' in row ? row.subRows : undefined
                  }
                  globalFilterFn={pricingReviewGlobalFilterFn}
                  headerPadding={theme.sk.spacing.spaceSmall}
                  initialState={{
                    columnPinning: {
                      left: [ColumnId.Name],
                    },
                  }}
                  isActive
                  isStickyRow={isStickyRow}
                  layoutWidth={tableLayoutWidth ?? undefined}
                  onExpandedChange={onExpandedChange}
                  onSortingChange={onSortingChange}
                  renderHeaderRow={renderHeaderRow}
                  renderRow={renderRow}
                  state={{
                    columnOrder,
                    columnVisibility,
                    expanded,
                    globalFilter,
                    sorting,
                  }}
                />
              </View>
            </Suspense>
          )}
        </View>
      </AppPageLayout>
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  actionsWithPanelOpen: {
    paddingRight: PRICING_FILTER_PANEL_WIDTH,
  },
  container: {
    flex: 1,
    gap: theme.sk.spacing.spaceMedium,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  containerWithPanelOpen: {
    minWidth: PRICING_CONTENT_MIN_WIDTH + PRICING_FILTER_PANEL_WIDTH,
    paddingRight: PRICING_FILTER_PANEL_WIDTH,
  },
  headerCell: {
    alignItems: 'flex-end',
    height: PRICING_TABLE_LAYOUT.HEADER_HEIGHT,
  },
  iconButton: {
    borderWidth: 0,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
  },
  loadingContent: {
    flex: 1,
    gap: 42,
  },
  nameWithBadgeWrapper: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    justifyContent: 'space-between',
  },
  nestedNameWrapper: {
    paddingLeft: theme.sk.spacing.spaceLarge,
  },
  parentNameWrapper: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  tableWrapper: {
    flex: 1,
    position: 'relative',
  },
  topBarActions: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
